import { toUtcParsedDate } from './dateUtils';

// Eagerly import every .webp in src/images/fallback as its bundled URL.
// Vite resolves this glob at build time, so new images dropped into the
// folder are picked up automatically.
const fallbackImages = Object.values(
    import.meta.glob('../images/fallback/*.webp', { eager: true, import: 'default' })
);


const HelperFunctions = () => {

    // djb2 string hash — deterministic, fast, no external deps.
    const hashString = (str) => {
        let hash = 5381;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) + hash) + str.charCodeAt(i);
        }
        return Math.abs(hash);
    };

    const getFallbackImageSource = (key) => {
        if (key != null && key !== '') {
            return fallbackImages[hashString(String(key)) % fallbackImages.length];
        }
        return fallbackImages[Math.floor(Math.random() * fallbackImages.length)];
    };

    const kFormatter = (num) => {
        return Math.abs(num) > 999 ? Math.sign(num) * ((Math.abs(num) / 1000).toFixed(1)) + 'k' : Math.sign(num) * Math.abs(num)
    }

    const dateFormatter = (datestr, yearOnly = false) => {
        const theDate = new Date(typeof datestr === 'string' && !datestr.endsWith('Z') ? datestr.replace(' ', 'T') + 'Z' : datestr);

        if (theDate.valueOf() === 0 || isNaN(theDate.getTime())) {
            return 'N/A';
        }

        if (yearOnly) {
            return theDate.getFullYear();
        }

        const suffix = {
            one: 'st',
            two: 'nd',
            few: 'rd',
            other: 'th',
        };

        const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric' }).format(theDate).slice(0, 3);
        const pluralRule = new Intl.PluralRules('en-GB', { type: 'ordinal' }).select(theDate.getDate());
        const month = new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(theDate);
        const year = theDate.getFullYear();

        return `${day}${suffix[pluralRule]} ${month} ${year}`;
    };


    const ageFormatter = (dateOfBirth) => {
        const dob = new Date(dateOfBirth);
        const diffMs = Date.now() - dob.getTime();
        const ageDt = new Date(diffMs);
        return Math.abs(ageDt.getUTCFullYear() - 1970);
    };

    const dateFormatterRelative = (datestr) => {

        let date = new Date(typeof datestr === 'string' && !datestr.endsWith('Z') ? datestr.replace(' ', 'T') + 'Z' : datestr);

        var ONE_DAY = 1000 * 60 * 60 * 24;

        // Calculate the difference in milliseconds
        var differenceMs = Math.abs(new Date().valueOf() - date);

        // Convert back to days
        var daysDifference = Math.round(differenceMs / ONE_DAY);

        if (0 === daysDifference) {
            return 'today'
        } else if (1 === daysDifference) {
            return 'yesterday'
        } else {
            return daysDifference + ' days ago'
        }


    };

    const formatFixtureDate = (dateString, fullWeekday = false, withSuffix = false) => {
        const date = new Date(dateString.replace(' ', 'T') + 'Z');
        const options = { timeZone: 'UTC' };
        const weekday = date.toLocaleDateString("en-GB", { ...options, weekday: fullWeekday ? "long" : "short" }); // "Fri" or "Friday"
        const day = date.toLocaleDateString("en-GB", { ...options, day: "numeric" }); // "24"
        const month = date.toLocaleDateString("en-GB", { ...options, month: "short" }); // "Oct"
        const dayStr = withSuffix ? `${day}${getDaySuffix(Number(day))}` : day;
        return `${weekday}, ${dayStr} ${month}`;
    };

    const getDaySuffix = (day) => {
        if (day >= 11 && day <= 13) return "th";
        switch (day % 10) {
            case 1: return "st";
            case 2: return "nd";
            case 3: return "rd";
            default: return "th";
        }
    };

    const formatFixtureDateYear = (dateString) => {
        const date = new Date(dateString.replace(' ', 'T') + 'Z');
        const options = { timeZone: 'UTC' };
        const weekday = date.toLocaleDateString("en-GB", { ...options, weekday: "short" });
        const day = date.toLocaleDateString("en-GB", { ...options, day: "numeric" });
        const month = date.toLocaleDateString("en-GB", { ...options, month: "short" });
        const year = date.getFullYear();
        return `${weekday}, ${day} ${month} ${year}`; // "Fri, 24 Oct 2026"
    };

    const formatTime24 = (dateString) => {
        const date = new Date(dateString.replace(' ', 'T').replace('Z', '') + 'Z');
        return date.toLocaleTimeString("en-GB", { timeZone: 'UTC', hour: "2-digit", minute: "2-digit" });
    };

    const formatTime12 = (dateString) => {
        const date = new Date(dateString.replace(' ', 'T').replace('Z', '') + 'Z');
        return date.toLocaleTimeString("en-US", { timeZone: 'UTC', hour: "2-digit", minute: "2-digit", hour12: true }).replace(/\s/g, '');
    };

    const getSeasonYear = () => {
        const now = new Date();
        const year = now.getFullYear();
        const nextYear = (year + 1).toString().slice(-2);
        return `${year}/${nextYear}`;
    };

    function number_format(number, decimals, dec_point, thousands_sep) {
        // Strip all characters but numerical ones.
        number = (number + '').replace(/[^0-9+\-Ee.]/g, '');
        var n = !isFinite(+number) ? 0 : +number,
            prec = !isFinite(+decimals) ? 0 : Math.abs(decimals),
            sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep,
            dec = (typeof dec_point === 'undefined') ? '.' : dec_point,
            s = '',
            toFixedFix = function (n, prec) {
                var k = Math.pow(10, prec);
                return '' + Math.round(n * k) / k;
            };
        // Fix for IE parseFloat(0.55).toFixed(0) = 0;
        s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');
        if (s[0].length > 3) {
            s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
        }
        if ((s[1] || '').length < prec) {
            s[1] = s[1] || '';
            s[1] += new Array(prec - s[1].length + 1).join('0');
        }
        return s.join(dec);
    }

    const amountFormatter = (value) => {
        if (value === 0 || value === '0' || value === '£0' || value === '£0.0') {
            return '£0';
        }

        if (!value) return value;

        // Remove pound symbol and commas
        let clean = value.toString().replace(/£|,/g, '');

        // Detect and store if it's negative
        const isNegative = clean.startsWith('-');
        if (isNegative) clean = clean.slice(1);

        let num;
        // Handle M or K
        if (clean.endsWith('M')) {
            num = parseFloat(clean.replace('M', '')) * 1_000_000;
        } else if (clean.endsWith('K')) {
            num = parseFloat(clean.replace('K', '')) * 1_000;
        } else {
            num = parseFloat(clean);
        }

        if (isNaN(num)) return value;

        // Handle exactly 0
        if (num === 0) return '£0';

        const formatted = (num >= 1_000_000)
            ? `${number_format(num / 1_000_000, 1)}M`
            : (num >= 1_000)
                ? `${number_format(num / 1_000, 0)}K` // changed to 0 decimal places
                : number_format(num, 1);

        return `£${isNegative ? '-' : ''}${formatted}`;
    }

    const currencyFormatter = (locale, currency, value) => {
        return new Intl.NumberFormat(locale, { style: "currency", currency: currency }).format(
            value,
        )
    }

    const numberRoundMax = (source, n) => {
        let places = Math.pow(10, n);

        return Math.round(source * places) / places;
    }

    const stripeCurrencyEncode = (currency) => {
        switch (currency.toUpperCase()) {
            case 'GBP':
                return '£';
            case 'EUR':
                return '€';
            case 'USD':
                return '$';
            default:
                return '';
        }
    }

    const addIntervalToDate = (date, interval) => {
        switch (interval) {
            case 'day':
                return date.setDay(date.getDay() + 1);
            case 'month':
                return date.setMonth(date.getMonth() + 1);
            case 'year':
                return date.setYear(date.getYear() + 1);
            default:
                return date;
        }
    }

    const containsAny = (arr1, arr2) => {
        //test array maybe null
        if (!arr2) {
            return false;
        }
        return arr1.some(item => arr2.includes(item));
    }

    // Get specific position name for the current statsPosition context
    const getSpecificPosition = (player) => {
        if (!player || !player.positions || !player.statsPosition) {
            return getMainPosition(player) || player.positions[0].name || "Unknown";
        }

        // Find the position that matches the current statsPosition group
        const specificPosition = findPositionByStatPosition(player.positions, player.statsPosition);

        // If no match found, fall back to main position
        return specificPosition || getMainPosition(player) || player.positions[0].name || "Unknown";
    };

    const doesUserHaveSubscription = (userRoles) => {

        return containsAny(['sportskinetic-light-user', 'sportskinetic-freemium-user', 'sportskinetic-enterprise-user', 'admin'], userRoles);
    }

    const doesUserHavePaidForSubscription = (userRoles) => {

        return containsAny(['sportskinetic-light-user', 'sportskinetic-freemium-user'], userRoles);
    }

    const isUserBetaTester = (userRoles) => {
        return containsAny(['admin', 'sportskinetic-enterprise-user'], userRoles);
    }

    const statsKeyFormatter = (stat, sentenceCasing, statValue) => {
        let sentence = stat.replace(new RegExp('_', 'g'), ' ').replace(new RegExp('-', 'g'), ' ').split(/(?=[A-Z])/).join(' ').toLowerCase();

        // Remove plural suffix if statValue is 1 (singular)
        if (statValue === 1) {
            if (sentence.endsWith('es')) {
                sentence = sentence.slice(0, -2);
            } else if (sentence.endsWith('s') && !sentence.endsWith('ss')) {
                sentence = sentence.slice(0, -1);
            }
        }

        if (!sentenceCasing) {
            return sentence;
        } else {
            return sentence.charAt(0).toUpperCase() + sentence.slice(1);
        }

    };

    const normalizeValue = (val, min, max) => {
        if (typeof val !== "number" || isNaN(val)) return 50;
        if (max === min) return 50; // avoid divide by zero
        return ((val - min) / (max - min)) * 100;
    }

    const statsConfigLabels = {
        PASS: { key: "passing", label: "Passing" },
        CCR: { key: "chance_creation", label: "Chance creation" },
        GS: { key: "goalscoring", label: "Goalscoring" },
        POSS: { key: "possession", label: "Possession" },
        AER: { key: "aerial", label: "Aerial" },
        DUA: { key: "duels", label: "Duels" },
        POSI: { key: "positioning", label: "Positioning" },
        DIS: { key: "discipline", label: "Discipline" },
    };


    const positionInitialsMap = {
        "Goalkeeper": "GK",
        "Left-Back": "LB",
        "Right-Back": "RB",
        "Centre-Back": "CB",
        "Left Wing Back": "LWB",
        "Right Wing Back": "RWB",
        "Central Midfielder": "CM",
        "Defensive Midfielder": "DM",
        "Left Winger": "LW",
        "Right Winger": "RW",
        "Left Midfielder": "LM",
        "Right Midfielder": "RM",
        "Attacking Midfielder": "AM",
        "Centre Forward": "CF",
        "Striker": "SK",
        "Second Striker": "SS",
    };

    const positionStatsPositionMap = [
        { position: "Goalkeeper", statsPosition: "G" },
        { position: "Left-Back", statsPosition: "D" },
        { position: "Right-Back", statsPosition: "D" },
        { position: "Centre-Back", statsPosition: "D" },
        { position: "Left Wing Back", statsPosition: "D" },
        { position: "Right Wing Back", statsPosition: "D" },
        { position: "Central Midfielder", statsPosition: "M" },
        { position: "Defensive Midfielder", statsPosition: "M" },
        { position: "Attacking Midfielder", statsPosition: "M" },
        { position: "Left Midfielder", statsPosition: "M" },
        { position: "Right Midfielder", statsPosition: "M" },
        { position: "Left Winger", statsPosition: "F" },
        { position: "Right Winger", statsPosition: "F" },
        { position: "Centre Forward", statsPosition: "F" },
        { position: "Striker", statsPosition: "F" },
        { position: "Second Striker", statsPosition: "F" },
    ];

    const getInitialsPosition = (name = "") => {

        const cleaned = name.replace(/-/g, ' ').trim(); // remove dashes
        return positionInitialsMap[name] || positionInitialsMap[cleaned] || name;
    };

    // Helper function to get stat value from the stats array
    const getPlayerStatValue = (player, statKey) => {
        if (!player.stats) return 0;
        const statObj = player.stats.find(stat => stat[statKey] !== undefined);
        return statObj ? statObj[statKey] : null;
    };

    // Helper function to get main position
    const getMainPosition = (player) => {

        if (!player.positions || !Array.isArray(player.positions)) return "";
        const mainPosition = player.positions.find(pos => pos.pivot?.is_main === 1);
        return mainPosition ? mainPosition.name :
            player.positions[0] ? player.positions[0].name :
                player.statsPosition || "";
    };

    const shortPlayerName = (firstName = "", lastName = "", maxLength = 15) => {

        if (!firstName && !lastName) return "";

        if (firstName && !lastName) {
            let display = `${firstName}`;

            // Trim if still too long
            if (display.length > maxLength) {
                display = display.slice(0, maxLength - 1) + "…";
            }
            return display;

        } else {

            // First initial
            const firstInitial = firstName ? firstName.charAt(0).toUpperCase() + "." : "";

            // Split last name by space (to handle double-barrel / multiple last names)
            const parts = lastName.trim().split(" ");

            if (parts.length === 1) {
                // Just one last name -> return normally
                let display = `${firstInitial} ${parts[0]}`;
                if (display.length > maxLength) {
                    display = display.slice(0, maxLength - 1) + "…";
                }
                return display;
            } else {
                // Multiple last names -> keep first, shorten the rest
                const main = parts[0];
                const shortened = parts.slice(1).map(p => p.charAt(0).toUpperCase()).join("");
                let display = `${firstInitial} ${main} ${shortened}`;

                // Trim if still too long
                if (display.length > maxLength) {
                    display = display.slice(0, maxLength - 1) + "…";
                }
                return display;
            }
        }
    }

    const formatNotificationMessage = (message) => {
        if (!message) return message;

        // Keywords that should be bold (event types)
        const boldKeywords = ['insight', 'insights', 'latest', 'news', 'score', 'goal', 'match', 'fixture', 'report'];

        // Split message into words while preserving punctuation
        const words = message.split(/(\s+|[,.:;!?])/);

        return words.map((word, index) => {
            const cleanWord = word.trim();

            // Skip empty strings and whitespace
            if (!cleanWord) return word;

            // Check if word is a bold keyword (case-insensitive)
            const isBoldKeyword = boldKeywords.some(keyword =>
                cleanWord.toLowerCase() === keyword.toLowerCase()
            );

            // Check if word is capitalized (likely a proper noun like player name)
            const isCapitalized = /^[A-Z][a-z]+/.test(cleanWord);

            // Apply bold to keywords and capitalized words (but not common words like "New", "Check", "The", etc.)
            const commonWords = ['new', 'the', 'check', 'out', 'latest', 'for', 'a', 'an', 'and', 'or'];
            const shouldBeBold = isBoldKeyword || (isCapitalized && !commonWords.includes(cleanWord.toLowerCase()));

            return (
                <span key={index} className={shouldBeBold ? 'fw-semi-bold' : 'fw-normal'}>
                    {word}
                </span>
            );
        });
    };

    const stringTruncate = (str, n = 100) => {
        if (!str) return ''; // Return an empty string if str is undefined or null
        return (str.length > n) ? str.slice(0, n - 1) + '...' : str;
    };

    const termTruncate = (str, n = 100) => {
        if (!str) return ''; // Return an empty string if str is undefined or null
        return (str.length > n) ? `"${str.slice(0, n - 1)}...` : `"${str}"`;
    };
    function formatValue(value) {
        if (value === undefined || value === null) {
            return 'N/A';
        }
        if (value === 0) {
            return '0';
        }
        if (typeof value === 'number') {
            return value.toLocaleString();
        }
        return value;
    }

    const getStyleClass = (transferType) => {
        const lowerType = transferType?.toLowerCase();
        const isLoanType = lowerType === 'loan' || lowerType === 'end of loan';
        const isPermanentType = lowerType === 'permanent transfer';

        // Default values
        let borderClass = '';
        let bgClass = 'bg-dark-mode-200';

        // if (isLoanType) {
        //     borderClass = 'border-fio-error-400'; 
        //     bgClass = 'bg-fio-error-400';
        // } else if (isPermanentType) {
        //     borderClass = 'border-fio-success-400';
        //     bgClass = 'bg-fio-success-400';
        // }

        if (isLoanType || isPermanentType) {
            borderClass = 'border-fio-success-400';
            bgClass = 'bg-fio-success-400';
        }

        return { borderClass, bgClass };
    };

    const valueToQueryStringParam = (value) => {
        if (typeof value === 'string' || typeof value === 'number') {
            return value.toString();
        } else {
            return JSON.stringify(value)
        }

    }

    const buildQueryStringFromFilterObj = (filterObj, excludeOrigin) => {

        let stateString = '';
        let foundProperties = [];

        if (0 < window.location.search.length) {

            const params = new URLSearchParams(window.location.search);

            //loop through existing query string params and modify them if appropiate
            for (const [key, value] of params.entries()) {

                if (key in filterObj) {
                    stateString = stateString + key + "=" + valueToQueryStringParam(filterObj[key]) + '&';
                    foundProperties.push(key);
                } else {
                    stateString = stateString + key + "=" + value + '&';
                }
            }

        }

        //now add those properties that are not present in the querystring
        for (const property in filterObj) {
            if (-1 === foundProperties.indexOf(property)) {
                stateString = stateString + property + "=" + valueToQueryStringParam(filterObj[property]) + '&';
            }
        }


        if (0 < stateString.length) {
            stateString = '?' + stateString.substring(0, stateString.length - 1);
        } else {
            if (!excludeOrigin)
                stateString = window.location.href.split('?')[0];
        }

        return stateString;

    }
    const firstNameExtracted = (first_name) => {
        return first_name ? first_name.split(' ')[0] : '';
    }

    /**
     * Checks if a given element has a CSS class.
     * 
     * @param element the element
     * @param className the CSS class name
     */

    const hasClass = (element, className) => {
        if (element.classList) return !!className && element.classList.contains(className);
        return (" " + (element.className.baseVal || element.className) + " ").indexOf(" " + className + " ") !== -1;
    }

    /**
     * Adds a CSS class to a given element.
     * 
     * @param element the element
     * @param className the CSS class name
     */

    const addClass = (element, className) => {
        if (element.classList) element.classList.add(className); else if (!hasClass(element, className)) if (typeof element.className === 'string') { element.className = element.className + " " + className; } else { element.setAttribute('class', ((element.className && element.className.baseVal) || '') + " " + className) };
    }

    const replaceClassName = (origClass, classToRemove) => {
        return origClass.replace(new RegExp("(^|\\s)" + classToRemove + "(?:\\s|$)", 'g'), '$1').replace(/\s+/g, ' ').replace(/^\s*|\s*$/g, '');
    }

    /**
     * Removes a CSS class from a given element.
     * 
     * @param element the element
     * @param className the CSS class name
     */
    const removeClass = (element, className) => {
        if (element.classList) {
            element.classList.remove(className);
        } else if (typeof element.className === 'string') {
            element.className = replaceClassName(element.className, className);
        } else {
            element.setAttribute('class', replaceClassName((element.className && element.className.baseVal) || '', className));
        }
    }

    const addClassToBody = (el, className) => {
        addClass((el.ownerDocument || document).body, className);
    }
    const removeClassFromBody = (el, className) => {
        removeClass((el.ownerDocument || document).body, className);
    }

    const getBodyScrollbarWidth = (ownerDocument) => {
        const window = ownerDocument.defaultView;
        return Math.abs(window.innerWidth - ownerDocument.documentElement.clientWidth);
    }

    const getPriceFromPriceVariant = (priceVariation) => {
        // switch (priceVariation) {
        //     case 'control':
        //         return 9.99;
        //     case 'variant1':
        //         return 4.99;
        //     case 'variant2':
        //         return 2.99;
        //     default:
        //         return 9.99;
        // }
        switch (priceVariation) {
            case 'control':
                return 4.99;
            case 'variant1':
                return 4.99;
            case 'variant2':
                return 4.99;
            default:
                return 9.99;
        }
    }

    const findPositionByStatPosition = (positions, statPosition) => {
        if (!positions || !Array.isArray(positions) || !statPosition) {
            return null;
        }

        for (const position of positions) {
            const match = positionStatsPositionMap.find(
                mapping => mapping.position === position.name && mapping.statsPosition === statPosition
            );
            if (match) {
                return match.position;
            }
        }

        return null;
    }
    /**
         * Filters fixtures to only show games whose kick-off is still in the
         * future (minute-precise), and sorts by date/time first, then by home
         * team name alphabetically. match_date is a UTC timestamp (the 'Z' may
         * be omitted, so parse via toUtcParsedDate).
         *
         * @param {Array} fixtures - Array of fixture objects with match_date property
         * @returns {Array} Filtered and sorted fixtures
         */
    const filterAndSortFixtures = (fixtures) => {
        if (!fixtures || !Array.isArray(fixtures)) return [];

        const now = new Date();
        const GRACE_MS = 100 * 60 * 1000; // keep fixtures visible for 100 minutes post-kickoff

        const toDate = (dateStr) => toUtcParsedDate(dateStr);

        return fixtures
            .filter(f => {
                const d = toDate(f.match_date);
                return d && d.getTime() + GRACE_MS >= now.getTime();
            })
            .sort((a, b) => {
                const dateA = toDate(a.match_date);
                const dateB = toDate(b.match_date);
                if (dateA.getTime() !== dateB.getTime()) {
                    return dateA - dateB;
                }
                return a.home_team.name.localeCompare(b.home_team.name);
            });
    }

    /**
     * Gets the next upcoming fixture that hasn't started yet.
     * Falls back to first sorted fixture if all have started.
     *
     * @param {Array} sortedFixtures - Already filtered and sorted fixtures
     * @returns {Object|null} The next upcoming fixture
     */
    const getNextUpcomingFixture = (sortedFixtures) => {
        if (!sortedFixtures || sortedFixtures.length === 0) return null;

        const now = new Date();

        // Find first fixture that hasn't started yet
        const upcoming = sortedFixtures.find(f => toUtcParsedDate(f.match_date) > now);
        return upcoming || sortedFixtures[0];
    }

    const competitionDisplayName = (competition) => {
        if (!competition) return '';
        //return competition.id === 2327 ? `${competition.name}  ${competition.season?.start_year}` : competition.name;
        return competition.name;
    }

    // Build the venue/stage caption shown above a fixture (e.g.
    // "Matchweek 5 · Old Trafford, Manchester, England • Home").
    // Accepts the parts directly because callers nest them differently
    // (fixture.competition vs fixtureDetails.fixture.competition, etc.).
    const getFixtureVenueText = ({ venue, competition, stage } = {}) => {
        const venueAddress = [venue?.name, venue?.city, venue?.country].filter(Boolean).join(', ');

        const stageLabel = competition?.id === 1
            ? (stage?.round ? `Matchweek ${stage.round}` : null)
            : stage?.type === 'knockout' ? stage?.name :  stage?.group;

        const venueDisplay = [stageLabel, venueAddress].filter(Boolean).join(' · ');
        return venueDisplay
            ? venueDisplay + (venue?.locale ? ` • ${venue.locale}` : '')
            : '';
    };

    const marketToText = (market) => {
        switch (market) {
            case 'btts':
                return 'Both teams to score';
            case '0.5':
                return 'At least 1 goal scored';
            case '1.5':
                return 'More than 1 goal scored';
            case '2.5':
                return 'More than 2 goals scored';
            case '3.5':
                return 'More than 3 goals scored';
            default:
                return '';

        }
    }

    const slugifyForUrl = (text) => {
        return (text || '')
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .replace(/-+$/, '');
    }

    const trimAtSentence = (input, maxLength = 180) => {
        if (!input) return '';
        if (input.length <= maxLength) return input;
        const window = input.slice(0, maxLength);
        const sentenceEnd = Math.max(window.lastIndexOf('. '), window.lastIndexOf('! '), window.lastIndexOf('? '));
        if (sentenceEnd > 60) return window.slice(0, sentenceEnd + 1);
        const wordEnd = window.lastIndexOf(' ');
        return (wordEnd > 0 ? window.slice(0, wordEnd) : window).replace(/[\s,;:]+$/, '') + '…';
    };

    return { getFallbackImageSource, filterAndSortFixtures, getNextUpcomingFixture, kFormatter, dateFormatter, formatFixtureDate, formatFixtureDateYear, formatTime24, formatTime12, getSeasonYear, dateFormatterRelative, formatNotificationMessage, stringTruncate, termTruncate, ageFormatter, shortPlayerName, statsConfigLabels, getStyleClass, amountFormatter, currencyFormatter, numberRoundMax, stripeCurrencyEncode, addIntervalToDate, containsAny, getSpecificPosition, doesUserHaveSubscription, doesUserHavePaidForSubscription, statsKeyFormatter, normalizeValue, getInitialsPosition, getPlayerStatValue, getMainPosition, formatValue, buildQueryStringFromFilterObj, firstNameExtracted, addClassToBody, removeClassFromBody, getBodyScrollbarWidth, getPriceFromPriceVariant, findPositionByStatPosition, marketToText, slugifyForUrl, isUserBetaTester, trimAtSentence, competitionDisplayName, getFixtureVenueText };

}
export default HelperFunctions;